import { createRouter } from "@tanstack/solid-router";

import { AuthState } from "./lib/auth";
import { ErrorFallback } from "./components/ui/error-fallback";
import { Loader } from "./components/ui/loader";
import { routeTree } from "./routeTree.gen";

export interface RouterContext {
  auth: AuthState;
}

export const createRouterContext = (): RouterContext => ({
  auth: {
    inviteUrl: null,
    user: null,
    session: null,
    loading: true,
    requiresJoin: false,
  },
});


export function getRouter() {
  const context = createRouterContext();

  const router = createRouter({
    context,
    routeTree,
    // Prefetch a route's lazy chunk (and loader) when the user hovers or touches
    // its link, so a new route isn't a cold chunk fetch at click time. Data still
    // lives in TanStack Query, so let Query own freshness rather than the router
    // caching loader results.
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    // Fill the route-transition gap (chunk load + beforeLoad) with the branded
    // loader instead of an empty grey Outlet. pendingMs delays it slightly so a
    // fast/preloaded nav doesn't flash it; pendingMinMs keeps it up long enough
    // to not strobe once shown.
    defaultPendingComponent: () => <Loader />,
    defaultPendingMs: 150,
    defaultPendingMinMs: 400,
    // Catch errors at the route level so they're always styled, never bare [object Object]
    // text. Includes lazy-route chunk recovery.
    defaultErrorComponent: ({ error, reset }) => <ErrorFallback error={error} reset={reset} />,
  });

  return router;
}

declare module "@tanstack/solid-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}